import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import ApiConstants from 'src/app/shared/constants/api.constants';
import { catchError } from 'rxjs/operators';
import {
  DisputeFilterParams,
  DisputeResponse,
  DisputeStatusResponse,
  DisputeTypeResponse,
  DisputeUpdateResponse,
  GenericDisputeDetailsResponse,
} from 'src/app/shared/model/dispute.model';
import { CommonService } from 'src/app/shared/services/common.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class DisputeService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getDisputeType(): Observable<DisputeTypeResponse> {
    const API_URL = `${this.apiUrl}${ApiConstants.DISPUTE_TYPES}`;
    return this.http
      .get<DisputeTypeResponse>(API_URL)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getDisputeStatus(): Observable<DisputeStatusResponse> {
    const API_URL = `${this.apiUrl}${ApiConstants.DISPUTE_STATUS}`;
    return this.http
      .get<DisputeStatusResponse>(API_URL)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  updateDisputeType(disputeId: number, payload: object): Observable<DisputeUpdateResponse> {
    const API_URL = `${this.apiUrl}${ApiConstants.DISPUTE}/${disputeId}`;
    return this.http
      .put<DisputeUpdateResponse>(API_URL, payload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getDisputeData(
    pageNumber: number,
    pageSize: number,
    sortField?: string,
    sortOrder?: string,
  ): Observable<DisputeResponse> {
    let API_URL = `${this.apiUrl}${ApiConstants.DISPUTE}?PageNumber=${pageNumber}&PageSize=${pageSize}`;

    if (sortField !== null && sortField !== undefined) {
      API_URL += CommonService.getQuerySingleParams('SortBy', sortField);
    }

    if (sortOrder !== null && sortOrder !== undefined) {
      API_URL += CommonService.getQuerySingleParams('SortOrder', sortOrder);
    }

    return this.http
      .get<DisputeResponse>(API_URL)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFilteredDisputes(disputeFilterParams: DisputeFilterParams): Observable<DisputeResponse> {
    let API_URL = `${this.apiUrl}${ApiConstants.DISPUTE}?PageNumber=${disputeFilterParams.pageNumber}&PageSize=${disputeFilterParams.pageSize}`;

    // Append filters to the API URL if they are provided
    if (
      disputeFilterParams.userName !== null &&
      disputeFilterParams.userName !== '' &&
      disputeFilterParams.userName !== undefined
    ) {
      API_URL += CommonService.getQuerySingleParams('UserName', disputeFilterParams.userName);
    }

    if (disputeFilterParams.disputeTypes && disputeFilterParams.disputeTypes.length) {
      disputeFilterParams.disputeTypes.forEach((type) => {
        API_URL += `&DisputeTypes=${type}`;
      });
    }

    // Append each disputeStatus as a separate parameter
    if (disputeFilterParams.disputeStatus && disputeFilterParams.disputeStatus.length) {
      disputeFilterParams.disputeStatus.forEach((status) => {
        API_URL += `&DisputeStatus=${status}`;
      });
    }

    if (disputeFilterParams.sortField !== null && disputeFilterParams.sortField !== undefined) {
      API_URL += CommonService.getQuerySingleParams('SortBy', disputeFilterParams.sortField);
    }

    if (disputeFilterParams.sortOrder !== null && disputeFilterParams.sortOrder !== undefined) {
      API_URL += CommonService.getQuerySingleParams('SortOrder', disputeFilterParams.sortOrder);
    }

    if (
      disputeFilterParams.isExport !== null &&
      disputeFilterParams.isExport !== false &&
      disputeFilterParams.isExport !== undefined
    ) {
      API_URL += CommonService.getQuerySingleParams('isExport', disputeFilterParams.isExport);
    }

    return this.http
      .get<DisputeResponse>(API_URL)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getDisputeById(disputeId: string): Observable<GenericDisputeDetailsResponse> {
    const API_URL = `${this.apiUrl}${ApiConstants.DISPUTE}/${disputeId}`;
    return this.http
      .get<GenericDisputeDetailsResponse>(API_URL)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
}
